import React from "react";
import ProgressiveImage from "react-progressive-graceful-image";

/**
 * Reusable component for progressive image loading with blur effect
 *
 * @param {string} src - The full-resolution image source
 * @param {string} placeholder - The low-resolution placeholder image
 * @param {string} alt - Alternative text for the image
 * @param {string|number} width - Image width
 * @param {string|number} height - Image height
 * @param {React.CSSProperties} style - Additional styles for the image
 * @param {number} blurAmount - Blur amount in pixels (default: 10)
 * @param {string} transitionDuration - Transition duration (default: "0.5s")
 * @param {string} fetchPriority - Browser hint: "high", "low", "auto" (default: "auto")
 * @param {string} loading - Browser loading strategy: "lazy", "eager" (default: "lazy")
 * @param {function} onLoad - Callback when image finishes loading
 */
const BlurredProgressiveImage = ({
  src,
  placeholder,
  alt = "",
  width,
  height,
  style = {},
  blurAmount = 3,
  transitionDuration = "1s",
  fetchPriority = "auto",
  loading = "lazy",
  onLoad,
}) => {
  return (
    <ProgressiveImage src={src} placeholder={placeholder}>
      {(imageSrc, loading) => {
        if (!loading && onLoad) {
          onLoad();
        }

        // Combine blur effect with existing filter styles
        const existingFilter = style.filter || "";
        const blurFilter = loading ? `blur(${blurAmount}px)` : "blur(0)";
        const combinedFilter = existingFilter
          ? `${existingFilter} ${blurFilter}`
          : blurFilter;

        // Combine blur transition with existing transitions
        const blurTransition = `filter ${transitionDuration} ease-out`;
        const existingTransition = style.transition || "";
        const combinedTransition = existingTransition
          ? `${existingTransition}, ${blurTransition}`
          : blurTransition;

        return (
          <img
            style={{
              ...style,
              filter: combinedFilter,
              transition: combinedTransition,
            }}
            width={width}
            height={height}
            alt={alt}
            src={imageSrc}
            fetchpriority={fetchPriority}
            loading={loading}
          />
        );
      }}
    </ProgressiveImage>
  );
};

export default BlurredProgressiveImage;
